'use client';

import { formatFullDate } from '@schuelerhilfe/shared/src/utils/dayjs';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { WidgetHeading } from '../title-widget/WidgetHeading';
import { AppointmentContactForm } from './AppointmentContactForm';
import {
  Step,
  SlotsStatus,
  LocationStatus,
  bookingReducer,
  initialBookingState,
} from './bookingReducer';
import { DayPicker } from './DayPicker';
import { TimePicker } from './TimePicker';
import type { BookAppointmentResolved } from './types';
import { useBookingSubmit } from './useBookingSubmit';
import { Icon } from '@/components/Icon';
import { Section } from '@/components/Section';
import { StepIndicator } from '@/components/StepIndicator';
import { SubmittedCard } from '@/components/SubmittedCard';
import { ZipCity, type ZipCityLocation } from '@/components/ZipCity';
import { getPublicLocationBySlug, type LocationDto } from '@/lib/api';
import {
  TimeSlotMode,
  generateDefaultSlots,
  loadAppointmentSlots,
} from '@/lib/appointments';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { resolveCenterName } from '@/lib/location/centerName';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';

// Fallbacks when the CMS step labels are unset — the pre-CMS hardcoded texts.
const DEFAULT_CHAPTERS = [
  'Standort',
  'Wunschtermin wählen',
  'Kostenlose Beratung sichern',
];

// Fallback placeholder of the location field on pages without a Standort when
// the CMS `locationPlaceholder` is unset (ZipCity appends the required mark
// → "PLZ oder Ort eingeben *").
const DEFAULT_LOCATION_PLACEHOLDER = 'PLZ oder Ort eingeben';

// Chapter 0 (Standort) is the location step: pre-completed on Location Pages
// (the Standort comes from the render context), an actual step elsewhere.
// Day and Time are sub-steps of chapter 1; Form is chapter 2.
const stepChapter: Record<Step, number> = {
  location: 0,
  day: 1,
  time: 1,
  form: 2,
};

/** The location field's text once a Standort is chosen: "<PLZ> - <display name>". */
export function selectedLocationLabel(location: LocationDto): string {
  const postcode = location.postcode ?? location.zipCode;
  const name = resolveCenterName(location);
  return postcode ? `${postcode} - ${name}` : name;
}

const Spinner = () => (
  <div className="flex justify-center py-16">
    <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
  </div>
);

export const BookAppointmentWidget = ({
  resolved,
}: {
  resolved: BookAppointmentResolved;
}) => {
  const { entities } = useRenderContext();
  const pageLocation = entities.location;
  // Location Page: the Standort is known up front and never shown as a field.
  // City Page & co: no Standort in the render context → the visitor picks one
  // first, and the field stays visible on every step until submission.
  const locationPreselected = Boolean(pageLocation);
  const tForm = useTranslations('form');
  const tBooking = useTranslations('booking');
  const tCommon = useTranslations('common');

  const [state, dispatch] = useReducer(
    bookingReducer,
    pageLocation?.slug,
    (slug) => initialBookingState(slug, { withLocationStep: true }),
  );
  const {
    slots,
    slotsStatus,
    step,
    selectedDay,
    selectedTime,
    selectedMode,
    submitted,
    selectedLocation,
    locationStatus,
  } = state;

  const location = pageLocation ?? selectedLocation ?? undefined;

  const [locationQuery, setLocationQuery] = useState('');
  // Slug of the Standort fetch in flight; a stale response (the visitor
  // picked another result or edited the field meanwhile) is dropped.
  const pendingLocationSlug = useRef<string | null>(null);

  const { handleSubmit, isPending } = useBookingSubmit(
    dispatch,
    location?.slug,
    resolved.stepForm?.submitTracking,
    resolved.stepForm?.tracking,
    locationPreselected ? undefined : (location?.postcode ?? location?.zipCode),
  );

  const activeDays = useMemo(
    () =>
      slotsStatus === SlotsStatus.Fallback && location?.slug
        ? generateDefaultSlots()
        : slots,
    [slotsStatus, location?.slug, slots],
  );

  useEffect(() => {
    const slug = location?.slug;
    if (!slug) {
      return;
    }
    // A late response for a Standort the visitor has since replaced (or
    // cleared) must not overwrite the current one's slots.
    let active = true;
    loadAppointmentSlots(slug).then((days) => {
      if (!active) {
        return;
      }
      dispatch(
        days
          ? { type: 'SLOTS_LOADED', slots: days }
          : { type: 'SLOTS_FALLBACK' },
      );
    });
    return () => {
      active = false;
    };
  }, [location?.slug]);

  const handleLocationSelect = (picked: ZipCityLocation) => {
    pendingLocationSlug.current = picked.slug;
    dispatch({ type: 'LOCATION_LOADING' });
    getPublicLocationBySlug(picked.slug).then((full) => {
      if (pendingLocationSlug.current !== picked.slug) {
        return;
      }
      pendingLocationSlug.current = null;
      if (!full) {
        dispatch({ type: 'LOCATION_FAILED' });
        return;
      }
      setLocationQuery(selectedLocationLabel(full));
      dispatch({ type: 'LOCATION_SELECTED', location: full });
    });
  };

  const handleLocationTextChange = (value: string) => {
    setLocationQuery(value);
    // Any edit invalidates a pending fetch, a failed one, or a made selection
    // (unless the text still reads exactly as the selected Standort).
    const staleSelection =
      selectedLocation !== null &&
      value !== selectedLocationLabel(selectedLocation);
    if (
      pendingLocationSlug.current ||
      locationStatus !== LocationStatus.Idle ||
      staleSelection
    ) {
      pendingLocationSlug.current = null;
      dispatch({ type: 'LOCATION_CLEARED' });
    }
  };

  const activeChapter = stepChapter[step];
  const selectedDayData = activeDays.find((d) => d.dateIso === selectedDay);

  const chapters = [
    resolved.stepLabelLocation || DEFAULT_CHAPTERS[0],
    resolved.stepLabelDateTime || DEFAULT_CHAPTERS[1],
    resolved.stepLabelContact || DEFAULT_CHAPTERS[2],
  ];

  const chapterLabels = chapters.map((s, i) => {
    if (i === 0) {
      return location ? `${s}: ${resolveCenterName(location)}` : s;
    }
    if (i === 1 && step === Step.Form && selectedDayData && selectedTime) {
      const modeLabel =
        selectedMode === TimeSlotMode.Phone
          ? (resolved.pickerTexts?.timeSlotPhoneLabel ?? 'telefonisch')
          : (resolved.pickerTexts?.timeSlotLocationLabel ?? 'vor Ort');
      return `${selectedDayData.day} ${formatFullDate(selectedDayData.dateIso)}, ${selectedTime} Uhr, ${modeLabel}`;
    }
    return s;
  });

  const zipCity =
    location?.zipCode && location?.cityName
      ? `${location.zipCode} ${location.cityName}`
      : '';

  // SH-344 item 6.1. Two different behaviours, because the two cases mean
  // different things to a visitor:
  //
  // - On the Standort's **own** page the whole widget goes. A partner who
  //   switched the form off is saying this Standort does not take online
  //   appointments, and a heading over an explanation of an absent form is
  //   noise on a page that is about that Standort anyway.
  // - Reached through the **PLZ search** from another page, the widget stays
  //   and says so. The visitor asked about this Standort and deserves an
  //   answer; silently showing nothing would read as a broken form.
  //
  // Absent means the partner expressed no preference, which shows the form.
  const appointmentsOff = location?.renderAppointmentForm === false;
  const hideEntirely = locationPreselected && appointmentsOff;

  const isLoading =
    slotsStatus === SlotsStatus.Loading ||
    locationStatus === LocationStatus.Loading;
  // A Standort with the form switched off offers no days, whatever the slot
  // endpoint answered: the partner's decision outranks whatever availability
  // upstream still reports, and the day picker must not appear under the
  // explanation of why there is no form.
  const hasDays = !appointmentsOff && activeDays.length > 0;
  // Location Pages render the steps only once slots exist (unchanged); the
  // location-step variant shows the stepper and field from the start.
  const showSteps = !submitted && (hasDays || !locationPreselected);
  const noSlotsForSelection =
    !locationPreselected &&
    selectedLocation !== null &&
    slotsStatus === SlotsStatus.Idle &&
    !hasDays;

  if (hideEntirely) {
    return null;
  }

  return (
    <Section>
      {/* Inside the widget, so the partner's toggle takes it down with the
          form rather than leaving it over nothing (SH-344 item 6.2). */}
      {resolved.heading && <WidgetHeading resolved={resolved.heading} />}

      {locationPreselected && isLoading && <Spinner />}

      {submitted && (
        <SubmittedCard>
          <p className="h3 mb-4 font-medium">
            {resolved.confirmationTitle
              ? resolved.confirmationTitle
                  .replace('{salutation}', tForm(submitted.salutation))
                  .replace('{name}', submitted.lastname)
              : tForm('thankYouName', {
                  salutation: tForm(submitted.salutation),
                  name: submitted.lastname,
                })}
          </p>

          {resolved.confirmationBody && (
            <div className="mb-10 text-gray-500">
              <RichText nodes={resolved.confirmationBody} />
            </div>
          )}

          {/*
            Each term is a bare icon — no visible word says which of the three
            rows is the address, which the date and which the time. So these
            icons are named rather than hidden, or the summary reads as three
            unlabelled values (70.14 § Icons).
          */}
          <dl className="mx-auto inline-block text-left text-sm">
            <div className="mb-6 flex items-start gap-4">
              <dt className="mt-1 text-gray-500">
                <Icon
                  src="/icons/map-pin.svg"
                  size={20}
                  label={tCommon('summaryLocation')}
                />
              </dt>
              <dd>
                <div>{resolveCenterName(location)}</div>
                <div>{location?.street ?? ''}</div>
                <div>{zipCity}</div>
              </dd>
            </div>
            <div className="mb-6 flex items-center gap-4">
              <dt className="text-gray-500">
                <Icon
                  src="/icons/calendar.svg"
                  size={20}
                  label={tCommon('summaryDate')}
                />
              </dt>
              <dd>{submitted.date}</dd>
            </div>
            <div className="flex items-center gap-4">
              <dt className="text-gray-500">
                <Icon
                  src="/icons/clock.svg"
                  size={20}
                  label={tCommon('summaryTime')}
                />
              </dt>
              <dd>{submitted.time} Uhr</dd>
            </div>
          </dl>
        </SubmittedCard>
      )}

      {showSteps && (
        <>
          <StepIndicator
            labels={chapterLabels}
            activeStep={activeChapter}
            // `StepIndicator` now reports which completed step was clicked.
            // This widget only supports going back to chapter 1 (Wunschtermin);
            // chapter 0 is the predetermined Standort and stays inert.
            onBack={
              step === Step.Form
                ? (target: number) => {
                    if (target === 1) {
                      dispatch({ type: 'STEP_SET', step: Step.Time });
                    }
                  }
                : undefined
            }
          />

          {!locationPreselected && (
            <div className="mx-auto mb-10 w-full max-w-xl">
              <ZipCity
                value={locationQuery}
                onValueChange={handleLocationTextChange}
                onSelect={handleLocationSelect}
                required
                placeholder={
                  resolved.locationPlaceholder || DEFAULT_LOCATION_PLACEHOLDER
                }
                error={
                  locationStatus === LocationStatus.Error
                    ? tBooking('locationLoadFailed')
                    : undefined
                }
              />
            </div>
          )}

          {!locationPreselected && isLoading && <Spinner />}

          {/* The picked Standort's own wording, not the page's: the visitor
              asked about that Standort, and each partner explains it in their
              own words. Falls back to the standard "no slots" line when the
              partner switched the form off without writing one, so the visitor
              is never left with a silent gap. Ranked above `noSlotsForSelection`
              because "switched off" is a decision and "no slots" is a state. */}
          {!locationPreselected && appointmentsOff && (
            <p className="text-center whitespace-pre-line">
              {location?.appointmentsDisabledText ||
                resolved.pickerTexts?.noSlotsText ||
                tBooking('noSlotsAvailable')}
            </p>
          )}

          {noSlotsForSelection && !appointmentsOff && (
            <p className="text-center">
              {resolved.pickerTexts?.noSlotsText ||
                tBooking('noSlotsAvailable')}
            </p>
          )}

          {hasDays && step === Step.Day && (
            <DayPicker
              availableDays={activeDays}
              selectedDay={selectedDay}
              onSelect={(dateIso) =>
                dispatch({ type: 'DAY_SELECTED', dateIso })
              }
              onNext={() => {
                pushCmsEventTracking(resolved.stepDay?.tracking);
                dispatch({ type: 'STEP_SET', step: Step.Time });
              }}
              ctaLabel={resolved.stepDay?.ctaLabel}
              heading={resolved.pickerTexts?.dayPickerHeading}
            />
          )}

          {step === Step.Time && selectedDayData && (
            <TimePicker
              day={selectedDayData}
              selectedTime={selectedTime}
              selectedMode={selectedMode}
              onSelect={({ time, mode }) =>
                dispatch({ type: 'TIME_SELECTED', time, mode })
              }
              onBack={() => dispatch({ type: 'STEP_SET', step: Step.Day })}
              onNext={() => {
                pushCmsEventTracking(resolved.stepTime?.tracking);
                dispatch({ type: 'STEP_SET', step: Step.Form });
              }}
              ctaLabel={resolved.stepTime?.ctaLabel}
              backLabel={resolved.stepTime?.backLabel}
              heading={resolved.pickerTexts?.timePickerHeading}
              locationGroupLabel={resolved.pickerTexts?.timeSlotLocationLabel}
              phoneGroupLabel={resolved.pickerTexts?.timeSlotPhoneLabel}
            />
          )}

          {step === Step.Form && selectedDayData && selectedTime && (
            <AppointmentContactForm
              onBack={() => dispatch({ type: 'STEP_SET', step: Step.Time })}
              onSubmit={(data) =>
                handleSubmit(data, selectedDayData.dateIso, selectedTime)
              }
              isPending={isPending}
              ctaLabel={resolved.stepForm?.ctaLabel}
              backLabel={resolved.stepForm?.backLabel}
              privacyText={resolved.stepForm?.privacyText}
              heading={resolved.stepForm?.heading}
              formTexts={resolved.stepForm?.formTexts}
            />
          )}
        </>
      )}
    </Section>
  );
};
